import {
  useParams,
  useHistory,
  useLocation,
  generatePath,
} from "react-router-dom";
import { useMemo } from "react";
import { RouteParams } from "../types";
import { FUTUREBILITY_COCKPIT_URL } from "../../constants";

type PathUpdate = Partial<RouteParams>;

type QueryUpdate = Record<string, string | undefined>;

const getQueryParamsObject = (search: string): Record<string, string> => {
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

const buildPath = ({
  clientId,
  testId,
  module,
  category,
  cubeSide,
}: RouteParams) =>
  generatePath(
    `${FUTUREBILITY_COCKPIT_URL}/:clientId/:testId/:module?/:category?/:cubeSide?`,
    {
      clientId,
      testId,
      module,
      category,
      cubeSide,
    }
  );

export const useUrlState = () => {
  const history = useHistory();
  const location = useLocation();
  const routeParams = useParams<RouteParams>();

  const queryParams = useMemo(
    () => getQueryParamsObject(location.search),
    [location.search]
  );

  const path: RouteParams = {
    clientId: routeParams.clientId,
    testId: routeParams.testId,
    module: routeParams.module,
    category: routeParams.category,
    cubeSide: routeParams.cubeSide,
  };

  const updatePath = (updates: PathUpdate) => {
    const nextPathState = {
      ...path,
      ...updates,
    };

    const pathname = buildPath(nextPathState);
    const search = new URLSearchParams(location.search).toString();

    history.push(search ? `${pathname}?${search}` : pathname);
  };

  const updateQueryParams = (updates: QueryUpdate) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });

    history.push({
      pathname: location.pathname,
      search: searchParams.toString() ? `?${searchParams.toString()}` : "",
    });
  };

  return {
    path,
    updatePath,
    queryParams,
    updateQueryParams,
  };
};
